// API key console — business logic, framework-agnostic, zero React imports.
// Distinct from features/apikeys/logic.ts (personal "my keys" page) — this file backs the
// tenant-wide admin console under pages/apikey/.

import type { APIKey, APIKeyStatus, APIKeyPolicy } from '@core/types/apikey';

// ============================================
// Status display
// ============================================

export const API_KEY_STATUS_VARIANT: Record<string, 'success' | 'error' | 'warning' | 'default' | 'secondary'> = {
  API_KEY_STATUS_UNSPECIFIED: 'default',
  API_KEY_STATUS_ACTIVE: 'success',
  API_KEY_STATUS_REVOKED: 'error',
  API_KEY_STATUS_EXPIRED: 'secondary',
  API_KEY_STATUS_SUSPENDED: 'warning',
};

export function statusVariant(status?: APIKeyStatus | string): 'success' | 'error' | 'warning' | 'default' | 'secondary' {
  return API_KEY_STATUS_VARIANT[status ?? ''] ?? 'default';
}

export function statusLabelKey(status?: APIKeyStatus | string): string {
  switch (status) {
    case 'API_KEY_STATUS_ACTIVE': return 'apikey.status.active';
    case 'API_KEY_STATUS_REVOKED': return 'apikey.status.revoked';
    case 'API_KEY_STATUS_EXPIRED': return 'apikey.status.expired';
    case 'API_KEY_STATUS_SUSPENDED': return 'apikey.status.suspended';
    default: return 'apikey.status.unknown';
  }
}

// ============================================
// Business rules — what actions apply to a key's current status
// ============================================

export function canRevoke(key: APIKey): boolean {
  return key.status === 'API_KEY_STATUS_ACTIVE' || key.status === 'API_KEY_STATUS_SUSPENDED';
}

export function canDelete(key: APIKey): boolean {
  return key.status === 'API_KEY_STATUS_REVOKED' || key.status === 'API_KEY_STATUS_EXPIRED';
}

export function canSuspend(key: APIKey): boolean {
  return key.status === 'API_KEY_STATUS_ACTIVE';
}

export function canActivate(key: APIKey): boolean {
  return key.status === 'API_KEY_STATUS_SUSPENDED';
}

export function canRegenerate(key: APIKey): boolean {
  return key.status === 'API_KEY_STATUS_ACTIVE' || key.status === 'API_KEY_STATUS_SUSPENDED';
}

export function canEditKey(key: APIKey): boolean {
  return key.status !== 'API_KEY_STATUS_REVOKED';
}

// ============================================
// Permission matrix helpers (permission_matrix entries <-> Record<section, actions[]>)
// ============================================

export function permissionsFromKey(apiKey: APIKey): Record<string, string[]> {
  const entries = apiKey.permission_matrix?.entries;
  if (entries) {
    const result: Record<string, string[]> = {};
    for (const entry of entries) {
      if (entry.section && entry.actions?.length > 0) {
        result[entry.section] = [...entry.actions];
      }
    }
    return result;
  }
  return {};
}

export function permissionMatrixEntries(permissions: Record<string, string[]>): Array<{ section: string; actions: string[] }> {
  return Object.entries(permissions)
    .filter(([, actions]) => actions.length > 0)
    .map(([section, actions]) => ({ section, actions }));
}

export function permissionCount(permissions: Record<string, string[]>): number {
  return Object.values(permissions).filter((a) => a.length > 0).length;
}

// ============================================
// Create / edit form
// ============================================

export interface KeyFormState {
  name: string;
  description: string;
  permissions: Record<string, string[]>;
  neverExpires: boolean;
  expiresAt: string; // yyyy-mm-dd or empty
}

export const EMPTY_KEY_FORM: KeyFormState = {
  name: '',
  description: '',
  permissions: {},
  neverExpires: true,
  expiresAt: '',
};

export function validateKeyForm(data: KeyFormState): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!data.name.trim()) {
    errors.name = 'apikey.validation.nameRequired';
  } else if (data.name.length > 100) {
    errors.name = 'apikey.validation.nameTooLong';
  }
  if (data.description.length > 500) {
    errors.description = 'apikey.validation.descriptionTooLong';
  }
  if (!Object.values(data.permissions).some((a) => a.length > 0)) {
    errors.permissions = 'apikey.validation.permissionsRequired';
  }
  if (!data.neverExpires) {
    if (!data.expiresAt) {
      errors.expiresAt = 'apikey.validation.expirationRequired';
    } else {
      const d = new Date(data.expiresAt);
      if (isNaN(d.getTime()) || d <= new Date()) {
        errors.expiresAt = 'apikey.validation.expirationFuture';
      }
    }
  }
  return errors;
}

// ============================================
// Key policy form
// ============================================

export interface PolicyFormState {
  name: string;
  description: string;
  scopeEntityType: string;
  maxKeys: string;
  maxKeysPerUser: string;
  defaultTtlDays: string;
  maxTtlDays: string;
  allowNeverExpires: boolean;
  maxRequestsPerMinute: string;
  allowedPermissionSections: string[];
  requireIpRestrictions: boolean;
  requireExpiration: boolean;
}

export const EMPTY_POLICY_FORM: PolicyFormState = {
  name: '',
  description: '',
  scopeEntityType: '',
  maxKeys: '',
  maxKeysPerUser: '',
  defaultTtlDays: '',
  maxTtlDays: '',
  allowNeverExpires: true,
  maxRequestsPerMinute: '',
  allowedPermissionSections: [],
  requireIpRestrictions: false,
  requireExpiration: false,
};

export function policyFormFromPolicy(p: APIKeyPolicy): PolicyFormState {
  return {
    name: p.name ?? '',
    description: p.description ?? '',
    scopeEntityType: p.scope_entity_type ?? '',
    maxKeys: p.max_keys != null ? String(p.max_keys) : '',
    maxKeysPerUser: p.max_keys_per_user != null ? String(p.max_keys_per_user) : '',
    defaultTtlDays: p.default_ttl_days != null ? String(p.default_ttl_days) : '',
    maxTtlDays: p.max_ttl_days != null ? String(p.max_ttl_days) : '',
    allowNeverExpires: p.allow_never_expires ?? true,
    maxRequestsPerMinute: p.max_requests_per_minute != null ? String(p.max_requests_per_minute) : '',
    allowedPermissionSections: p.allowed_permission_sections ?? [],
    requireIpRestrictions: p.require_ip_restrictions ?? false,
    requireExpiration: p.require_expiration ?? false,
  };
}

export function validatePolicyForm(data: PolicyFormState): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!data.name.trim()) errors.name = 'apikey.validation.nameRequired';
  return errors;
}

function toOptionalInt(value: string): number | undefined {
  if (!value.trim()) return undefined;
  const n = parseInt(value, 10);
  return isNaN(n) ? undefined : n;
}

export function policyFormToRequestFields(data: PolicyFormState): Record<string, unknown> {
  return {
    name: data.name.trim(),
    description: data.description.trim() || undefined,
    scope_entity_type: data.scopeEntityType.trim() || undefined,
    max_keys: toOptionalInt(data.maxKeys),
    max_keys_per_user: toOptionalInt(data.maxKeysPerUser),
    default_ttl_days: toOptionalInt(data.defaultTtlDays),
    max_ttl_days: toOptionalInt(data.maxTtlDays),
    allow_never_expires: data.allowNeverExpires,
    max_requests_per_minute: toOptionalInt(data.maxRequestsPerMinute),
    allowed_permission_sections: data.allowedPermissionSections,
    require_ip_restrictions: data.requireIpRestrictions,
    require_expiration: data.requireExpiration,
  };
}

// ============================================
// Formatters
// ============================================

export function formatDate(iso?: string): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(undefined, { year: 'numeric', month: 'short', day: 'numeric' }).format(date);
}

export function formatDateTime(iso?: string): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  }).format(date);
}

export function formatExpiration(key: APIKey): string {
  if (key.never_expires) return '—';
  return formatDate(key.expires_at);
}

export function formatKeyPrefix(prefix?: string): string {
  return prefix ? `${prefix}…` : '---';
}

export function formatLatency(ms?: number): string {
  if (ms == null) return '—';
  return ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(2)} s`;
}

export function formatErrorRate(errorCount?: number, totalCount?: number): string {
  if (!totalCount) return '—';
  const rate = ((errorCount ?? 0) / totalCount) * 100;
  return `${rate.toFixed(1)}%`;
}

// ============================================
// Typed confirmation phrases
// ============================================

export function revokeConfirmPhrase(key: APIKey): string {
  return key.name;
}

export function deletePolicyConfirmPhrase(policy: APIKeyPolicy): string {
  return policy.name ?? '';
}
